import { left, Result, right } from '@/types/repository/common/result';
import {
  mapMessageToApiMessage,
  APIMessage,
  Message,
  AiArtifact,
  ChatDynamicParams,
} from '@/modules/chat/types/message';
import {
  ApiChatResponse,
  ChatResponse,
  DeferredToolResults,
  mapApiChatResponseToChatResponse,
} from '../types/chatResponse';

export class InnovachatRepository {
  serviceUrl: string;
  private readonly SESSION_TIMEOUT_MINUTES = 5;

  constructor(serviceUrl: string) {
    this.serviceUrl = serviceUrl;
  }

  private parseDynamicParams(dynamicParams: ChatDynamicParams | undefined):
    | {
      query_parameters: Record<string, unknown> | undefined;
      path_parameters: Record<string, unknown> | undefined;
      request_body: Record<string, unknown> | undefined;
    }
    | undefined {
    if (!dynamicParams) {
      return undefined;
    }

    return {
      query_parameters: dynamicParams?.queryParameters,
      path_parameters: dynamicParams?.pathParameters,
      request_body: dynamicParams?.requestBody,
    };
  }

  async chat({
    jwt,
    message,
    llmConfigId,
    sessionId,
    context = '',
    collections = [],
    apiIds = [],
    tags = [],
    dynamicParams,
    tools,
    authInstanceId,
    deferredToolResults,
  }: {
    jwt?: string;
    deferredToolResults?: DeferredToolResults;
    message?: Message;
    context?: string;
    collections?: (number | string)[];
    apiIds?: (number | string)[];
    tags?: string[];
    sessionId?: string;
    llmConfigId: number | string;
    dynamicParams?: ChatDynamicParams;
    tools?: any[];
    authInstanceId: number | string;
  }): Promise<Result<ChatResponse>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.SESSION_TIMEOUT_MINUTES * 60000);
    const authHeaders: Record<string, string> = jwt ? { Authorization: `Bearer ${jwt}` } : {};

    try {
      const response = await fetch(
        `${this.serviceUrl}/chat/conversation?auth_instance_id=${authInstanceId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          signal: controller.signal,
          body: JSON.stringify({
            parameters: {
              llm_config: llmConfigId,
              messages: message ? [mapMessageToApiMessage(message)] : [],
              collection_ids: collections,
              api_ids: apiIds,
              context: context,
              tags: tags,
              session_id: sessionId,
              dynamic_params: this.parseDynamicParams(dynamicParams),
              tools: tools,
              deferred_tool_results: deferredToolResults,
            },
          }),
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        return left(new Error(response.statusText));
      }

      const mappedResponse = mapApiChatResponseToChatResponse(await response.json());

      return right(mappedResponse);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        return left(error);
      }
      return left(new Error('Unknown error during chat'));
    }
  }
}
